import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTeamDashboard } from '../TeamDashboardContext';
import { motion } from 'motion/react';
import { apiDelete, apiGet, apiPost } from '../api';
import './team.css';

const KudosConfettiCanvas: React.FC<{ active: boolean; onComplete: () => void }> = ({ active, onComplete }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#f43f5e', '#6366f1', '#10b981', '#fbbf24', '#a855f7', '#0ea5e9'];
    const particles = Array.from({ length: 120 }).map(() => ({
      x: Math.random() * canvas.width,
      y: Math.random() * -100 - 20,
      r: Math.random() * 6 + 4,
      d: Math.random() * canvas.height,
      color: colors[Math.floor(Math.random() * colors.length)],
      tilt: Math.random() * 10 - 5,
      tiltAngleIncremental: Math.random() * 0.07 + 0.02,
      tiltAngle: 0,
      w: Math.random() * 12 + 6,
      h: Math.random() * 12 + 6,
      vx: Math.random() * 4 - 2,
      vy: Math.random() * 3 + 4,
    }));

    let animationFrameId: number;
    let frames = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let activeParticles = 0;

      particles.forEach((p) => {
        p.tiltAngle += p.tiltAngleIncremental;
        p.y += p.vy;
        p.x += p.vx;
        p.tilt = Math.sin(p.tiltAngle) * 12;

        if (p.y < canvas.height + 20) {
          activeParticles++;
        }

        ctx.beginPath();
        ctx.lineWidth = p.r;
        ctx.strokeStyle = p.color;
        ctx.moveTo(p.x + p.tilt + p.r / 2, p.y);
        ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 2);
        ctx.stroke();
      });

      frames++;
      if (activeParticles > 0 && frames < 180) {
        animationFrameId = requestAnimationFrame(draw);
      } else {
        onComplete();
      }
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [active, onComplete]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        width: '100vw',
        height: '100vh',
      }}
    />
  );
};

type TeamRole = 'owner' | 'lead' | 'dev' | 'designer' | 'qa' | 'devops' | 'pm';

interface TeamMemberView {
  id: string;
  initials: string;
  name: string;
  email?: string;
  role: TeamRole;
  hoursThisWeek: number;
  budgetSpent: number;
  hourlyRate: number;
  qualityScore: number;
  prs?: number;
  commits?: number;
  isOwner: boolean;
}

type LogType = 'commit' | 'pr' | 'log' | 'review';

interface MemberLogEntry {
  time: string;
  type: LogType;
  dot: 'green' | 'amber' | 'red' | 'blue';
  title: string;
  desc: string;
  tags: Array<{ c: 'g' | 'a' | 'r' | 'b' | 'n'; t: string }>;
  hrs: number;
  cost: number;
}

interface MemberLogDay {
  day: string;
  entries: MemberLogEntry[];
}

interface MemberLogsPack {
  days: MemberLogDay[];
}

interface ProfileTask {
  name: string;
  project: string;
  date: string;
  hrs: number;
  pending?: boolean;
}

interface PendingInvite {
  id: string;
  email: string;
  role: TeamRole;
  hourlyRate: number;
  status: 'pending' | 'accepted' | 'expired';
  createdAt: string;
  message?: string;
  joinUrl?: string;
  inviteEmailSent?: boolean;
  inviteEmailError?: string | null;
  inviteEmailProviderId?: string | null;
}

interface ActivityItem {
  id: string;
  memberId?: string;
  memberInitials?: string;
  memberName?: string;
  action?: string;
  detail?: string;
  timestamp?: string;
}

const DEMO_ACTIVITY: ActivityItem[] = [
  { id: 'a1', memberId: 'm-ak', memberInitials: 'AK', memberName: 'Arjun Kumar', action: 'opened sprint plan', detail: 'Sprint 4 backlog prioritized and assigned', timestamp: new Date(Date.now() - 90 * 60 * 1000).toISOString() },
  { id: 'a2', memberId: 'm-sr', memberInitials: 'SR', memberName: 'Sneha Rao', action: 'reviewed pull request', detail: 'PR #18 JWT refresh patch approved', timestamp: new Date(Date.now() - 70 * 60 * 1000).toISOString() },
  { id: 'a3', memberId: 'm-rm', memberInitials: 'RM', memberName: 'Rahul Mehta', action: 'pushed commit', detail: 'feat(auth): add session guard fallback', timestamp: new Date(Date.now() - 50 * 60 * 1000).toISOString() },
  { id: 'a4', memberId: 'm-pk', memberInitials: 'PK', memberName: 'Priya Kapoor', action: 'updated test suite', detail: 'Added 14 regression tests for login flow', timestamp: new Date(Date.now() - 35 * 60 * 1000).toISOString() },
  { id: 'a5', memberId: 'm-ak', memberInitials: 'AK', memberName: 'Arjun Kumar', action: 'generated invoice preview', detail: 'Weekly invoice summary generated for 4 members', timestamp: new Date(Date.now() - 20 * 60 * 1000).toISOString() },
];

const DEMO_MEMBERS: TeamMemberView[] = [
  { id: 'm-ak', initials: 'AK', name: 'Arjun Kumar', email: 'arjun@scrudix.dev', role: 'owner', hoursThisWeek: 12.5, budgetSpent: 6250, hourlyRate: 500, qualityScore: 88, prs: 3, isOwner: true },
  { id: 'm-sr', initials: 'SR', name: 'Sneha Rao', email: 'sneha@scrudix.dev', role: 'lead', hoursThisWeek: 18, budgetSpent: 7200, hourlyRate: 400, qualityScore: 71, prs: 2, isOwner: false },
  { id: 'm-rm', initials: 'RM', name: 'Rahul Mehta', email: 'rahul@scrudix.dev', role: 'dev', hoursThisWeek: 8, budgetSpent: 4000, hourlyRate: 500, qualityScore: 44, prs: 1, isOwner: false },
  { id: 'm-pk', initials: 'PK', name: 'Priya Kapoor', email: 'priya@scrudix.dev', role: 'dev', hoursThisWeek: 4, budgetSpent: 2000, hourlyRate: 500, qualityScore: 91, prs: 2, isOwner: false },
];

const DEMO_MEMBER_LOGS: Record<string, MemberLogsPack> = {
  AK: {
    days: [
      {
        day: 'Today',
        entries: [
          { time: '11:42', type: 'commit', dot: 'green', title: 'feat: Add rate limiting to auth endpoints', desc: 'src/auth/middleware.ts · +124 -38 lines', tags: [{ c: 'g', t: 'merged' }, { c: 'n', t: 'main' }], hrs: 2, cost: 1000 },
          { time: '09:15', type: 'log', dot: 'blue', title: 'Daily standup & sprint planning', desc: 'Team sync · recorded 2h', tags: [{ c: 'b', t: 'meeting' }], hrs: 2, cost: 1000 },
        ],
      },
      {
        day: 'Yesterday',
        entries: [
          { time: '18:30', type: 'pr', dot: 'green', title: 'PR #17 — Refactor dashboard API layer', desc: '6 files changed · 3 reviewers · merged in 2h', tags: [{ c: 'g', t: 'merged' }, { c: 'n', t: '+312 -88' }], hrs: 3, cost: 1500 },
          { time: '15:10', type: 'review', dot: 'blue', title: 'Reviewed PR #14 by Priya — Payment flow', desc: 'Left 4 comments · approved', tags: [{ c: 'b', t: 'approved' }, { c: 'n', t: 'review' }], hrs: 1, cost: 500 },
          { time: '13:00', type: 'log', dot: 'amber', title: 'Architecture planning — microservices migration', desc: 'Wrote RFC doc · design review', tags: [{ c: 'a', t: 'planning' }], hrs: 2, cost: 1000 },
        ],
      },
    ],
  },
  SR: {
    days: [
      {
        day: 'Today',
        entries: [
          { time: '12:25', type: 'pr', dot: 'green', title: 'PR #19 — Session timeout fix', desc: 'Reviewed and merged with AK', tags: [{ c: 'g', t: 'merged' }, { c: 'n', t: 'auth' }], hrs: 2, cost: 800 },
          { time: '10:00', type: 'log', dot: 'blue', title: 'Lead standup & backlog grooming', desc: 'Sprint 4 week 3 planning session', tags: [{ c: 'b', t: 'lead duty' }], hrs: 2, cost: 800 },
        ],
      },
    ],
  },
  RM: {
    days: [
      {
        day: 'Today',
        entries: [
          { time: '11:00', type: 'log', dot: 'amber', title: 'Debugging session — auth token expiry', desc: 'Pair session with Sneha · root cause pending', tags: [{ c: 'a', t: 'debugging' }], hrs: 2, cost: 1000 },
          { time: '09:40', type: 'commit', dot: 'red', title: 'fix: rollback session cache patch', desc: 'Investigating regression in login flow', tags: [{ c: 'r', t: 'hotfix' }], hrs: 1, cost: 500 },
        ],
      },
    ],
  },
  PK: {
    days: [
      {
        day: 'Today',
        entries: [
          { time: '16:00', type: 'commit', dot: 'green', title: 'feat: Stripe webhook handler & retry logic', desc: 'src/payments/ · +280 -12 · clean implementation', tags: [{ c: 'g', t: 'clean' }, { c: 'n', t: 'feat/payments' }], hrs: 2, cost: 1000 },
          { time: '14:20', type: 'review', dot: 'blue', title: 'Reviewed Rahul auth patch', desc: '2 comments · requested edge-case test', tags: [{ c: 'b', t: 'review' }], hrs: 1, cost: 500 },
        ],
      },
    ],
  },
};

const DEMO_PROFILE_TASKS: Record<string, { done: ProfileTask[]; pending: ProfileTask[] }> = {
  AK: {
    done: [
      { name: 'JWT refresh token rotation', project: 'Backend API', date: 'Apr 25', hrs: 3.5 },
      { name: 'Dashboard API refactor merged', project: 'Backend API', date: 'Apr 24', hrs: 4 },
    ],
    pending: [
      { name: 'Redis caching layer', project: 'Backend API', date: 'Due May 10', hrs: 0, pending: true },
    ],
  },
  SR: {
    done: [
      { name: 'Dependency security patches', project: 'Auth Service', date: 'Apr 21', hrs: 2.5 },
    ],
    pending: [
      { name: 'Team retrospective report', project: 'Management', date: 'Due May 2', hrs: 0, pending: true },
    ],
  },
  RM: {
    done: [
      { name: 'Auth module scaffolding', project: 'Auth Service', date: 'Apr 17', hrs: 4 },
    ],
    pending: [
      { name: 'Auth bug fix - session WIP', project: 'Auth Service', date: 'Overdue', hrs: 0, pending: true },
    ],
  },
  PK: {
    done: [
      { name: 'Frontend auth UI components', project: 'Frontend', date: 'Apr 23', hrs: 2 },
    ],
    pending: [
      { name: 'E2E tests for login flow', project: 'Frontend', date: 'Due May 3', hrs: 0, pending: true },
    ],
  },
};

const ROLE_LABEL: Record<TeamRole, string> = {
  owner: 'Owner',
  lead: 'Lead',
  dev: 'Dev',
  designer: 'Design',
  qa: 'QA',
  devops: 'DevOps',
  pm: 'PM',
};

const ROLE_CLASS: Record<TeamRole, string> = {
  owner: 'pill pb',
  lead: 'pill pa',
  dev: 'pill pg',
  designer: 'pill pb',
  qa: 'pill pr',
  devops: 'pill pa',
  pm: 'pill pb',
};

const AVATAR_BG: Record<TeamRole, string> = {
  owner: 'rgba(77,156,248,.2)',
  lead: 'rgba(62,207,142,.2)',
  dev: 'rgba(245,166,35,.2)',
  designer: 'rgba(242,100,100,.2)',
  qa: 'rgba(176,110,245,.2)',
  devops: 'rgba(62,207,142,.2)',
  pm: 'rgba(77,156,248,.2)',
};

const AVATAR_FG: Record<TeamRole, string> = {
  owner: '#4d9cf8',
  lead: '#3ecf8e',
  dev: '#f5a623',
  designer: '#f26464',
  qa: '#b06ef5',
  devops: '#3ecf8e',
  pm: '#4d9cf8',
};

function payload<T>(res: unknown, fallback: T): T {
  if (res && typeof res === 'object' && 'data' in (res as Record<string, unknown>)) {
    return ((res as { data?: T }).data ?? fallback) as T;
  }
  return (res as T) ?? fallback;
}

function normalizeMember(raw: Record<string, unknown>): TeamMemberView {
  const name = String(raw.name ?? raw.memberName ?? 'Unknown');
  const resolvedRole = String(raw.role ?? 'dev').toLowerCase();
  const role = (['owner', 'lead', 'dev', 'designer', 'qa', 'devops', 'pm'].includes(resolvedRole) ? resolvedRole : 'dev') as TeamRole;
  const initials = String(raw.initials ?? name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase());
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    initials,
    name,
    email: raw.email ? String(raw.email) : undefined,
    role,
    hoursThisWeek: Number(raw.hoursThisWeek ?? raw.hours ?? 0),
    budgetSpent: Number(raw.budgetSpent ?? raw.totalSpent ?? 0),
    hourlyRate: Number(raw.hourlyRate ?? raw.rate ?? 0),
    qualityScore: Number(raw.codeQuality ?? raw.qualityScore ?? 80),
    prs: Number(raw.prsOpen ?? raw.prs ?? 0),
    commits: Number(raw.commits ?? 0),
    isOwner: role === 'owner',
  };
}

export default function TeamPage() {
  const { showToast, isReal, members, teamSlug, currentMemberName, projects, theme } = useTeamDashboard();
  const [mounted, setMounted] = useState(false);
  const [list, setList] = useState<TeamMemberView[]>(DEMO_MEMBERS);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([
    { id: 'inv-demo', email: 'dev@startup.com', role: 'dev', hourlyRate: 500, status: 'pending', createdAt: new Date(Date.now() - 3600_000).toISOString() },
  ]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'dev' as TeamRole, hourlyRate: 500, message: '' });
  const [inviteResult, setInviteResult] = useState<(PendingInvite & { inviteEmailSent?: boolean; inviteEmailError?: string | null; joinUrl?: string }) | null>(null);
  const [profileMember, setProfileMember] = useState<TeamMemberView | null>(null);
  const [profileTab, setProfileTab] = useState<'overview' | 'tasks' | 'logs' | 'report'>('overview');
  const [logsMember, setLogsMember] = useState<TeamMemberView | null>(null);
  const [logsFilter, setLogsFilter] = useState<'all' | LogType>('all');
  const [logsQuery, setLogsQuery] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; member: TeamMemberView } | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'working' | 'idle'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Kudos & recognition state
  const [kudosList, setKudosList] = useState<Array<{ sender: string; recipient: string; badge: string; note: string; date: string }>>([]);
  const [kudosTarget, setKudosTarget] = useState('');
  const [kudosBadge, setKudosBadge] = useState('🧠 Code Mentor');
  const [kudosNote, setKudosNote] = useState('');
  const [confettiActive, setConfettiActive] = useState(false);

  const handleSendKudos = (e: React.FormEvent) => {
    e.preventDefault();
    if (!kudosTarget || !kudosNote.trim()) {
      showToast('Kudos Error', 'Please select a team member and write a recognition note.', 'error');
      return;
    }
    const targetName = list.find(m => m.id === kudosTarget)?.name || 'Team member';
    const newKudos = {
      sender: currentMemberName || 'You',
      recipient: targetName,
      badge: kudosBadge,
      note: kudosNote.trim(),
      date: 'Just now'
    };
    setKudosList(prev => [newKudos, ...prev]);
    setKudosNote('');
    setConfettiActive(true);
    showToast('Kudos Awarded! 🏆', `You awarded a kudos badge to ${targetName}.`, 'success');
  };

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isReal) {
      // Real mode: use members from context, clear all demo data immediately
      if (Array.isArray(members) && members.length > 0) {
        setList(members.map(m => normalizeMember(m as unknown as Record<string, unknown>)));
      } else {
        setList([]);
      }
      setPendingInvites([]);
      setActivity([]);
      setKudosList([]);
      // Try to silently load invites and activity from API
      void (async () => {
        try {
          const [iRes, aRes] = await Promise.all([
            apiGet<unknown>(`/${teamSlug}/invites`),
            apiGet<unknown>(`/${teamSlug}/activity?limit=120`),
          ]);
          const i = payload<PendingInvite[]>(iRes, []);
          const a = payload<ActivityItem[]>(aRes, []);
          setPendingInvites(i.filter(inv => inv.status === 'pending'));
          setActivity(a);
        } catch {
          // Silently fail — no demo fallback in real mode
        }
      })();
      return;
    }
    // Demo mode
    if (Array.isArray(members) && members.length) {
      setList(members.map(m => normalizeMember(m as unknown as Record<string, unknown>)));
    } else {
      setList(DEMO_MEMBERS);
    }
    setKudosList([
      { sender: 'Arjun Kumar', recipient: 'Sneha Rao', badge: '🧠 Code Mentor', note: 'Incredible speed setting up the workspace boilerplate context API scaffolding. Kudos!', date: 'Today' },
      { sender: 'Priya Kapoor', recipient: 'Rahul Mehta', badge: '🐛 Bug Squasher', note: 'Found and hotfixed the JWT expire parameter offset mismatch within 5 minutes. Brilliant work.', date: 'Yesterday' }
    ]);
    setPendingInvites([
      { id: 'inv-demo', email: 'dev@startup.com', role: 'dev', hourlyRate: 500, status: 'pending', createdAt: new Date(Date.now() - 3600_000).toISOString(), message: 'Join us for sprint 4.' },
    ]);
    setActivity(DEMO_ACTIVITY);
  }, [isReal, members, teamSlug]);

  const filteredList = useMemo(() => {
    return list.filter(m => {
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (statusFilter === 'working' && m.hoursThisWeek === 0) return false;
      if (statusFilter === 'idle' && m.hoursThisWeek > 0) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        m.name.toLowerCase().includes(q) ||
        (m.email && m.email.toLowerCase().includes(q)) ||
        m.role.toLowerCase().includes(q) ||
        (ROLE_LABEL[m.role] && ROLE_LABEL[m.role].toLowerCase().includes(q))
      );
    });
  }, [list, roleFilter, statusFilter, searchQuery]);

  const kpiStats = useMemo(() => {
    const total = list.length;
    const activeNow = list.filter(m => m.hoursThisWeek > 0).length;
    const totalHours = list.reduce((sum, m) => sum + (m.hoursThisWeek || 0), 0);
    const totalBurn = list.reduce((sum, m) => sum + (m.hoursThisWeek > 0 ? (m.hourlyRate || 0) : 0), 0);
    return { total, activeNow, totalHours, totalBurn };
  }, [list]);

  useEffect(() => {
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLogsMember(null);
        setProfileMember(null);
        setShowInviteModal(false);
        setContextMenu(null);
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, []);

  const memberLogs = useMemo(() => {
    if (!logsMember) return [];
    return activity.filter(a =>
      a.memberId === logsMember.id
      || a.memberInitials === logsMember.initials
      || a.memberName === logsMember.name,
    );
  }, [activity, logsMember]);

  const logsPack = useMemo<MemberLogsPack>(() => {
    if (!logsMember) return { days: [] };
    const demo = DEMO_MEMBER_LOGS[logsMember.initials];
    if (!isReal && demo) return demo;

    const mapped: MemberLogEntry[] = memberLogs.map(log => {
      const text = `${log.action || ''} ${log.detail || ''}`.toLowerCase();
      const type: LogType = text.includes('pr') ? 'pr' : text.includes('review') ? 'review' : text.includes('commit') ? 'commit' : 'log';
      const hrs = 1;
      return {
        time: log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--',
        type,
        dot: type === 'commit' ? 'green' : type === 'pr' ? 'green' : type === 'review' ? 'blue' : 'amber',
        title: log.action || 'activity',
        desc: log.detail || '-',
        tags: [{ c: 'n', t: type }],
        hrs,
        cost: Math.round(hrs * (logsMember.hourlyRate || 0)),
      };
    });
    return { days: [{ day: 'Today', entries: mapped }] };
  }, [logsMember, memberLogs, isReal]);

  const visibleLogDays = useMemo(() => {
    const query = logsQuery.trim().toLowerCase();
    return logsPack.days
      .map(day => ({
        ...day,
        entries: day.entries.filter(entry => {
          if (logsFilter !== 'all' && entry.type !== logsFilter) return false;
          if (!query) return true;
          return (
            entry.title.toLowerCase().includes(query)
            || entry.desc.toLowerCase().includes(query)
            || entry.tags.some(tag => tag.t.toLowerCase().includes(query))
          );
        }),
      }))
      .filter(day => day.entries.length > 0);
  }, [logsPack, logsFilter, logsQuery]);

  const logsSummary = useMemo(() => {
    const entries = visibleLogDays.flatMap(day => day.entries);
    const totalHrs = entries.reduce((sum, e) => sum + e.hrs, 0);
    const totalCost = entries.reduce((sum, e) => sum + e.cost, 0);
    return { count: entries.length, totalHrs, totalCost };
  }, [visibleLogDays]);

  const achievements = useMemo(() => {
    if (!isReal) {
      return {
        prChampion: { name: 'Priya Kapoor', detail: 'Highest Quality score (91/100)' },
        bugSquasher: { name: 'Sneha Rao', detail: 'Merged 2 active PR structures' },
        velocityKing: { name: 'Arjun Kumar', detail: '18 hours logged this week' }
      };
    }

    if (list.length === 0) {
      return {
        prChampion: { name: 'None', detail: 'No members in workspace' },
        bugSquasher: { name: 'None', detail: 'No members in workspace' },
        velocityKing: { name: 'None', detail: 'No members in workspace' }
      };
    }

    // PR Champion: highest qualityScore
    const sortedByQuality = [...list].sort((a, b) => b.qualityScore - a.qualityScore);
    const topQuality = sortedByQuality[0];

    // Bug Squasher: highest prs (or commits if no prs)
    const sortedByPrs = [...list].sort((a, b) => {
      const aVal = (a.prs || 0) + (a.commits || 0) * 0.1;
      const bVal = (b.prs || 0) + (b.commits || 0) * 0.1;
      return bVal - aVal;
    });
    const topPrs = sortedByPrs[0];
    const bugSquasherDetail = topPrs.prs && topPrs.prs > 0
      ? `Merged ${topPrs.prs} active PR structures`
      : `${topPrs.commits || 0} commits this week`;

    // Velocity King: highest hoursThisWeek
    const sortedByHours = [...list].sort((a, b) => b.hoursThisWeek - a.hoursThisWeek);
    const topHours = sortedByHours[0];

    return {
      prChampion: {
        name: topQuality.name,
        detail: `Highest Quality score (${topQuality.qualityScore}/100)`
      },
      bugSquasher: {
        name: topPrs.name,
        detail: bugSquasherDetail
      },
      velocityKing: {
        name: topHours.name,
        detail: `${topHours.hoursThisWeek} hours logged this week`
      }
    };
  }, [isReal, list]);

  const profileLogs = useMemo(() => {
    if (!profileMember) return [] as MemberLogEntry[];
    if (isReal) {
      const logs = activity.filter(a =>
        a.memberId === profileMember.id
        || a.memberInitials === profileMember.initials
        || a.memberName === profileMember.name,
      );
      return logs.map(log => {
        const text = `${log.action || ''} ${log.detail || ''}`.toLowerCase();
        const type: LogType = text.includes('pr') ? 'pr' : text.includes('review') ? 'review' : text.includes('commit') ? 'commit' : 'log';
        return {
          time: log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--',
          type,
          dot: type === 'commit' ? 'green' : type === 'pr' ? 'green' : type === 'review' ? 'blue' : 'amber',
          title: log.action || 'activity',
          desc: log.detail || '-',
          tags: [{ c: 'n', t: type }],
          hrs: 1,
          cost: Math.round(1 * (profileMember.hourlyRate || 0)),
        } as MemberLogEntry;
      });
    }
    const demo = DEMO_MEMBER_LOGS[profileMember.initials];
    if (demo) return demo.days.flatMap(d => d.entries);
    return [] as MemberLogEntry[];
  }, [profileMember, activity, isReal]);

  const profileTasks = useMemo(() => {
    if (!profileMember) return { done: [], pending: [] };
    if (isReal) {
      const assignedProjects = projects.filter(p => p.members.includes(profileMember.initials));
      const done: ProfileTask[] = assignedProjects
        .filter(p => p.status === 'completed')
        .map(p => ({
          name: p.name,
          project: p.name,
          date: p.deadline || 'Completed',
          hrs: p.spent ? Math.round(p.spent / (profileMember.hourlyRate || 500)) : 0,
        }));
      const pending: ProfileTask[] = assignedProjects
        .filter(p => p.status !== 'completed')
        .map(p => ({
          name: p.name,
          project: p.name,
          date: p.deadline || 'Pending',
          hrs: p.budget && p.spent ? Math.round((p.budget - p.spent) / (profileMember.hourlyRate || 500)) : 0,
          pending: true
        }));
      return { done, pending };
    }
    return DEMO_PROFILE_TASKS[profileMember.initials] || { done: [], pending: [] };
  }, [profileMember, projects, isReal]);

  const profileComposite = useMemo(() => {
    if (!profileMember) return 0;
    const commits = profileTasks.done.length + Math.max(0, profileTasks.pending.length - 1);
    const prs = profileMember.prs ?? 0;
    return Math.round(
      (profileMember.qualityScore * 0.4)
      + (Math.min((commits / 6) * 100, 100) * 0.25)
      + (Math.min((prs / 3) * 100, 100) * 0.2)
      + (Math.min((profileMember.hoursThisWeek / 18) * 100, 100) * 0.15),
    );
  }, [profileMember, profileTasks]);

  const profilePerformance = useMemo(() => {
    if (!profileMember) return { velocity: 0, prRate: 0, utilization: 0 };
    const commits = profileTasks.done.length + Math.max(0, profileTasks.pending.length - 1);
    const prs = profileMember.prs ?? 0;
    return {
      velocity: Math.min(Math.round((commits / 6) * 100), 100),
      prRate: Math.min(Math.round((prs / 3) * 100), 100),
      utilization: Math.min(Math.round((profileMember.hoursThisWeek / 18) * 100), 100),
    };
  }, [profileMember, profileTasks]);

  const profileBadge = useMemo(() => {
    if (!profileMember) return 'Solid';
    if (profileMember.qualityScore >= 85) return 'Top Performer';
    if (profileMember.qualityScore >= 65) return 'Solid';
    return 'Needs Review';
  }, [profileMember]);

  async function submitInvite() {
    const email = inviteForm.email.trim().toLowerCase();
    if (!email) {
      showToast('Invite member', 'Email is required', 'error');
      return;
    }

    setInviteBusy(true);
    try {
      const selectedRole = inviteForm.role || 'dev';
      if (isReal) {
        const res = await apiPost<unknown>(`/${teamSlug}/invites`, {
          email,
          role: selectedRole,
          hourlyRate: Number(inviteForm.hourlyRate) || 500,
          message: inviteForm.message,
          invitedBy: currentMemberName || 'Workspace Admin',
        });
        const inv = payload<PendingInvite & { inviteEmailSent?: boolean; inviteEmailError?: string | null; joinUrl?: string }>(res, {
          id: crypto.randomUUID(), email, role: selectedRole, hourlyRate: inviteForm.hourlyRate, status: 'pending', createdAt: new Date().toISOString(),
        });
        setPendingInvites(prev => [inv, ...prev.filter(i => i.email !== inv.email)]);
        setInviteResult(inv);
        if (inv.inviteEmailSent === false) {
          showToast('Invite created', inv.inviteEmailError || 'Invite email failed, but you can copy the link below', 'error');
        } else {
          showToast('Invite sent', `Invite created for ${email}`, 'success');
        }
      } else {
        setPendingInvites(prev => [{ id: crypto.randomUUID(), email, role: selectedRole, hourlyRate: inviteForm.hourlyRate, status: 'pending', createdAt: new Date().toISOString(), message: inviteForm.message }, ...prev]);
        setActivity(prev => [{
          id: crypto.randomUUID(),
          memberId: 'm-me',
          memberInitials: 'AS',
          memberName: currentMemberName || 'Workspace Admin',
          action: 'sent invite',
          detail: `Invited ${email} as ${ROLE_LABEL[selectedRole]}`,
          timestamp: new Date().toISOString(),
        }, ...prev]);
        showToast('Invite sent', `Demo invite created for ${email}`, 'success');
      }
      setInviteForm({ email: '', role: 'dev', hourlyRate: 500, message: '' });
      if (!inviteResult) setShowInviteModal(false);
    } catch {
      showToast('Invite failed', 'Could not create invite', 'error');
    } finally {
      setInviteBusy(false);
    }
  }

  async function resendInvite(inv: PendingInvite) {
    try {
      if (isReal) {
        const res = await apiPost<unknown>(`/${teamSlug}/invites`, {
          email: inv.email,
          role: inv.role,
          hourlyRate: inv.hourlyRate,
          message: inv.message || '',
          invitedBy: currentMemberName,
        });
        const renewed = payload<PendingInvite & { inviteEmailSent?: boolean; inviteEmailError?: string | null }>(res, inv);
        setPendingInvites(prev => [renewed, ...prev.filter(i => i.id !== inv.id && i.email !== inv.email)]);
        if (renewed.inviteEmailSent === false) {
          showToast('Invite renewed', renewed.inviteEmailError || 'RESEND_API_KEY is not configured', 'error');
        } else {
          showToast('Resent', `Invite resent to ${inv.email}`, 'success');
        }
      } else {
        setPendingInvites(prev => prev.map(x => x.id === inv.id ? { ...x, createdAt: new Date().toISOString() } : x));
        showToast('Resent', `Demo invite resent to ${inv.email}`, 'success');
      }
    } catch {
      showToast('Resend failed', `Could not resend ${inv.email}`, 'error');
    }
  }

  async function removeMember(m: TeamMemberView) {
    if (m.isOwner) return;
    try {
      if (isReal) {
        await apiDelete(`/${teamSlug}/members/${m.id}`);
      }
      setList(prev => prev.filter(x => x.id !== m.id));
      setActivity(prev => [{
        id: crypto.randomUUID(),
        memberId: 'm-ak',
        memberInitials: 'AK',
        memberName: 'Arjun Kumar',
        action: 'removed member',
        detail: `${m.name} removed from workspace`,
        timestamp: new Date().toISOString(),
      }, ...prev]);
      showToast('Member removed', m.name, 'success');
    } catch {
      showToast('Remove failed', `Could not remove ${m.name}`, 'error');
    }
  }

  function copyText(value: string, successText: string) {
    navigator.clipboard.writeText(value).then(
      () => showToast('Copied', successText, 'success'),
      () => showToast('Copy failed', 'Clipboard permission denied', 'error'),
    );
  }

  function openProfile(member: TeamMemberView) {
    setProfileMember(member);
    setProfileTab('overview');
  }

  function openContextMenu(e: React.MouseEvent, member: TeamMemberView) {
    e.preventDefault();
    e.stopPropagation();
    const menuWidth = 210;
    const menuHeight = 300;
    const x = Math.min(e.clientX, window.innerWidth - menuWidth - 10);
    const y = Math.min(e.clientY, window.innerHeight - menuHeight - 10);
    setContextMenu({ x: Math.max(10, x), y: Math.max(10, y), member });
  }

  function ctxAction(action: 'profile' | 'logs' | 'assign' | 'message' | 'report' | 'remove') {
    if (!contextMenu) return;
    const target = contextMenu.member;
    setContextMenu(null);
    if (action === 'profile') openProfile(target);
    else if (action === 'logs') openLogs(target);
    else if (action === 'assign') showToast('Assign task', `Assigning task to ${target.name}...`, 'info');
    else if (action === 'message') showToast('Message', `Opening chat with ${target.name}...`, 'info');
    else if (action === 'report') showToast('Report', `Opening ${target.name} report...`, 'info');
    else if (action === 'remove') void removeMember(target);
  }

  function openLogs(member: TeamMemberView) {
    setLogsMember(member);
    setLogsFilter('all');
    setLogsQuery('');
  }

  return (
    <div className="td-team" data-theme={theme}>
      <div className="w-full flex flex-col gap-6" style={{ boxSizing: 'border-box' }}>
        {/* Hero Header */}
        <div className="team-hero-header">
          <div className="team-hero-title-group">
            <div className="team-hero-badge-tag">
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--tm-accent)' }} />
              <span>{teamSlug || 'Scrudix Dev'} · Team Directory</span>
            </div>
            <h1 className="team-hero-title">
              Team Intelligence & Operations
            </h1>
            <p className="team-hero-sub">
              <span>{list.length} registered members</span>
              <span>·</span>
              <span style={{ color: 'var(--tm-emerald)', fontWeight: 600 }}>{kpiStats.activeNow} working right now</span>
              <span>·</span>
              <span>Real-time work telemetry synchronized</span>
            </p>
          </div>
          <button className="team-cta-invite-btn" onClick={() => setShowInviteModal(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Invite Teammate</span>
          </button>
        </div>

        {/* 4-Card KPI Ribbon */}
        <div className="team-kpi-ribbon">
          <div className="team-kpi-card">
            <div className="team-kpi-icon-wrap" style={{ background: 'rgba(99, 102, 241, 0.1)', color: 'var(--tm-accent)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="team-kpi-content">
              <span className="team-kpi-label">Total Members</span>
              <span className="team-kpi-value">{kpiStats.total}</span>
              <span className="team-kpi-sub">Registered across team</span>
            </div>
          </div>

          <div className="team-kpi-card">
            <div className="team-kpi-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: 'var(--tm-emerald)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="team-kpi-content">
              <span className="team-kpi-label">Active Working</span>
              <span className="team-kpi-value" style={{ color: 'var(--tm-emerald)' }}>{kpiStats.activeNow}</span>
              <span className="team-kpi-sub">Currently clocked in</span>
            </div>
          </div>

          <div className="team-kpi-card">
            <div className="team-kpi-icon-wrap" style={{ background: 'rgba(245, 158, 11, 0.1)', color: 'var(--tm-amber)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="team-kpi-content">
              <span className="team-kpi-label">Hours Logged</span>
              <span className="team-kpi-value">{kpiStats.totalHours.toFixed(1)} hrs</span>
              <span className="team-kpi-sub">Sprint weekly total</span>
            </div>
          </div>

          <div className="team-kpi-card">
            <div className="team-kpi-icon-wrap" style={{ background: 'rgba(244, 63, 94, 0.1)', color: 'var(--tm-rose)' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div className="team-kpi-content">
              <span className="team-kpi-label">Active Burn Rate</span>
              <span className="team-kpi-value">₹{kpiStats.totalBurn.toLocaleString()}/hr</span>
              <span className="team-kpi-sub">Combined live rate</span>
            </div>
          </div>
        </div>

        {/* Achievements Leaderboard */}
        <div className="team-achievements-row">
          {/* PR Champion Card */}
          <motion.div 
            whileHover={{ y: -3 }}
            className="team-achievement-card"
          >
            <div className="team-achievement-medal" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <defs>
                  <linearGradient id="shieldGold" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#fbbf24" />
                    <stop offset="100%" stopColor="#d97706" />
                  </linearGradient>
                </defs>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="url(#shieldGold)" />
                <path d="M12 6l1.2 2.8H16l-2.2 1.8 0.8 3-2.6-2-2.6 2 0.8-3-2.2-1.8h3L12 6z" fill="#fff" />
              </svg>
            </div>
            <div className="team-achievement-meta">
              <span className="team-achievement-pill" style={{ color: 'var(--tm-amber)' }}>PR Champion 🏆</span>
              <div className="team-achievement-name">{achievements.prChampion.name}</div>
              <div className="team-achievement-desc">{achievements.prChampion.detail}</div>
            </div>
          </motion.div>

          {/* Bug Squasher Card */}
          <motion.div 
            whileHover={{ y: -3 }}
            className="team-achievement-card"
          >
            <div className="team-achievement-medal" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <defs>
                  <linearGradient id="bugGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#34d399" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
                <rect x="2" y="2" width="20" height="20" rx="6" fill="url(#bugGreen)" />
                <path d="M7 17l6-6M9 7l8 8M7 7l10 10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <div className="team-achievement-meta">
              <span className="team-achievement-pill" style={{ color: 'var(--tm-emerald)' }}>Bug Squasher 🛠️</span>
              <div className="team-achievement-name">{achievements.bugSquasher.name}</div>
              <div className="team-achievement-desc">{achievements.bugSquasher.detail}</div>
            </div>
          </motion.div>

          {/* Velocity King Card */}
          <motion.div 
            whileHover={{ y: -3 }}
            className="team-achievement-card"
          >
            <div className="team-achievement-medal" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <defs>
                  <linearGradient id="lightningViolet" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#818cf8" />
                    <stop offset="100%" stopColor="#4f39e3" />
                  </linearGradient>
                </defs>
                <circle cx="12" cy="12" r="10" fill="url(#lightningViolet)" />
                <path d="M13 3L6 13h5v8l7-10h-5V3z" fill="#fff" />
              </svg>
            </div>
            <div className="team-achievement-meta">
              <span className="team-achievement-pill" style={{ color: 'var(--tm-accent)' }}>Velocity King ⚡</span>
              <div className="team-achievement-name">{achievements.velocityKing.name}</div>
              <div className="team-achievement-desc">{achievements.velocityKing.detail}</div>
            </div>
          </motion.div>
        </div>

        {/* Search & Filter Controls Bar */}
        <div className="team-controls-bar">
          <div className="team-search-box">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'var(--tm-text-muted)' }}>
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="team-search-input"
              placeholder="Search team members by name, role, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{ background: 'none', border: 'none', color: 'var(--tm-text-muted)', cursor: 'pointer', fontSize: 13, padding: '0 4px' }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="team-filters-group">
            {['all', 'dev', 'lead', 'designer', 'qa', 'devops', 'pm'].map(role => (
              <button
                key={role}
                className={`team-filter-pill${roleFilter === role ? ' active' : ''}`}
                onClick={() => setRoleFilter(role)}
              >
                {role === 'all' ? 'All Roles' : ROLE_LABEL[role as TeamRole]}
              </button>
            ))}

            <div style={{ width: 1, height: 18, background: 'var(--tm-border)', margin: '0 4px' }} />

            <button
              className={`team-filter-pill${statusFilter === 'all' ? ' active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All Status
            </button>
            <button
              className={`team-filter-pill${statusFilter === 'working' ? ' active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'working' ? 'all' : 'working')}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              Active ({list.filter(m => m.hoursThisWeek > 0).length})
            </button>
            <button
              className={`team-filter-pill${statusFilter === 'idle' ? ' active' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'idle' ? 'all' : 'idle')}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} />
              Idle ({list.filter(m => m.hoursThisWeek === 0).length})
            </button>

            <div className="team-view-toggle">
              <button
                className={`team-view-btn${viewMode === 'grid' ? ' active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                </svg>
              </button>
              <button
                className={`team-view-btn${viewMode === 'table' ? ' active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table view"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Member Directory Grid / Table View */}
        {filteredList.length === 0 ? (
          <div style={{
            background: 'var(--tm-card)',
            border: '1px solid var(--tm-border)',
            borderRadius: '1.25rem',
            padding: '3rem 2rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <div style={{ fontSize: '2rem' }}>🔍</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--tm-text)' }}>No teammates matched your criteria</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--tm-text-secondary)', maxWidth: 380 }}>
              {searchQuery ? `No results found for "${searchQuery}".` : 'Try clearing your filters or invite a new team member to this workspace.'}
            </div>
            {(searchQuery || roleFilter !== 'all' || statusFilter !== 'all') && (
              <button
                className="btn-s"
                style={{ marginTop: '0.5rem', padding: '0.5rem 1rem' }}
                onClick={() => { setSearchQuery(''); setRoleFilter('all'); setStatusFilter('all'); }}
              >
                Reset all filters
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="team-member-grid">
            {filteredList.map((m, idx) => (
              <div
                key={m.id}
                className={`team-member-card fu${mounted ? ' in' : ''}`}
                style={{ transitionDelay: `${idx * 0.04}s`, cursor: 'pointer' }}
                onContextMenu={(e) => openContextMenu(e, m)}
              >
                <div className="tm-card-header">
                  <div className="tm-avatar-box" style={{ background: AVATAR_BG[m.role], color: AVATAR_FG[m.role] }}>
                    {m.initials}
                    <span className={`tm-status-indicator ${m.hoursThisWeek > 0 ? 'working' : 'idle'}`} />
                  </div>
                  <div className="tm-user-info">
                    <div className="tm-user-name">{m.name}</div>
                    <div 
                      className="tm-user-email" 
                      onClick={() => m.email && copyText(m.email, 'Email copied')}
                      title="Click to copy email"
                    >
                      <span>{m.email || `${m.name.toLowerCase().replace(/\s+/g, '.')}@scrudix.dev`}</span>
                    </div>
                  </div>
                  <span className={`tm-role-badge tm-role-${m.role}`}>
                    {ROLE_LABEL[m.role]}
                  </span>
                </div>

                <div className="tm-stats-grid">
                  <div className="tm-stat-cell">
                    <span className="tm-stat-label">Hours this week</span>
                    <span className="tm-stat-val">{m.hoursThisWeek} hrs</span>
                  </div>
                  <div className="tm-stat-cell">
                    <span className="tm-stat-label">Budget spent</span>
                    <span className="tm-stat-val">₹{m.budgetSpent.toLocaleString()}</span>
                  </div>
                  <div className="tm-stat-cell">
                    <span className="tm-stat-label">Hourly rate</span>
                    <span className="tm-stat-val">₹{m.hourlyRate}/hr</span>
                  </div>
                  <div className="tm-stat-cell">
                    <span className="tm-stat-label">Code quality</span>
                    <span className="tm-stat-val" style={{ color: m.qualityScore >= 80 ? 'var(--tm-emerald)' : m.qualityScore >= 60 ? 'var(--tm-amber)' : 'var(--tm-rose)' }}>
                      {m.qualityScore}/100
                    </span>
                  </div>
                </div>

                {/* Inline SVG Sparkline (Velocity Chart) */}
                <div className="tm-sparkline-row">
                  <div className="tm-sparkline-header">
                    <span>CODING VELOCITY</span>
                    <span className="tm-sparkline-delta">+{m.prs ? m.prs * 4 + 8 : 12}% this sprint</span>
                  </div>
                  <div style={{ width: '100%', height: 32, display: 'flex', alignItems: 'flex-end' }}>
                    <svg style={{ width: '100%', height: 28, overflow: 'visible' }} viewBox="0 0 100 30" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id={`sparkGrad-${m.id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={m.qualityScore >= 80 ? '#10b981' : '#6366f1'} stopOpacity="0.4" />
                          <stop offset="100%" stopColor={m.qualityScore >= 80 ? '#10b981' : '#6366f1'} stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d={`M 0,30 L 0,${30 - (m.hoursThisWeek * 1.2)} L 20,${20 + ((m.prs || 1) * 2)} L 40,${25 - (m.qualityScore * 0.1)} L 60,${15 + (m.hoursThisWeek * 0.4)} L 80,${28 - ((m.prs || 1) * 3)} L 100,${30 - (m.hoursThisWeek * 1.5)} L 100,30 Z`}
                        fill={`url(#sparkGrad-${m.id})`}
                      />
                      <path
                        d={`M 0,${30 - (m.hoursThisWeek * 1.2)} L 20,${20 + ((m.prs || 1) * 2)} L 40,${25 - (m.qualityScore * 0.1)} L 60,${15 + (m.hoursThisWeek * 0.4)} L 80,${28 - ((m.prs || 1) * 3)} L 100,${30 - (m.hoursThisWeek * 1.5)}`}
                        fill="none"
                        stroke={m.qualityScore >= 80 ? '#10b981' : '#6366f1'}
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="100"
                        cy={30 - (m.hoursThisWeek * 1.5)}
                        r="2.5"
                        fill={m.qualityScore >= 80 ? '#10b981' : '#6366f1'}
                      />
                    </svg>
                  </div>
                </div>

                <div className="tm-card-actions">
                  <button className="tm-btn-primary" onClick={() => openLogs(m)}>View logs</button>
                  <button
                    className="tm-btn-primary"
                    onClick={() => openProfile(m)}
                    onContextMenu={(e) => openContextMenu(e, m)}
                  >
                    Profile
                  </button>
                  {!m.isOwner && (
                    <button className="tm-btn-icon" title="Remove member" onClick={() => void removeMember(m)} style={{ color: 'var(--tm-rose)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Dash Invite Card */}
            <div 
              className="team-invite-card-hero"
              onClick={() => setShowInviteModal(true)}
            >
              <div className="team-invite-icon-circle">+</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--tm-text)' }}>Invite team member</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--tm-text-secondary)', maxWidth: 220 }}>
                Bring more colleagues into {teamSlug || 'Scrudix Dev'} to sync telemetry and code velocity
              </div>
            </div>
          </div>
        ) : (
          <div className="team-table-container">
            <table className="team-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Hours Logged</th>
                  <th>Hourly Rate</th>
                  <th>Total Spent</th>
                  <th>Quality</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((m) => (
                  <tr 
                    key={m.id}
                    onContextMenu={(e) => openContextMenu(e, m)}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div className="tm-avatar-box" style={{ width: 36, height: 36, fontSize: '0.85rem', background: AVATAR_BG[m.role], color: AVATAR_FG[m.role] }}>
                          {m.initials}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--tm-text)' }}>{m.name}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--tm-text-muted)', fontFamily: 'var(--mono)' }}>{m.email || 'teammate@scrudix.dev'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`tm-role-badge tm-role-${m.role}`}>
                        {ROLE_LABEL[m.role]}
                      </span>
                    </td>
                    <td>
                      <span style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '0.35rem', 
                        fontSize: '0.75rem', 
                        fontWeight: 600,
                        color: m.hoursThisWeek > 0 ? 'var(--tm-emerald)' : 'var(--tm-amber)' 
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.hoursThisWeek > 0 ? 'var(--tm-emerald)' : 'var(--tm-amber)' }} />
                        {m.hoursThisWeek > 0 ? 'Active' : 'Idle'}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{m.hoursThisWeek} hrs</td>
                    <td style={{ fontFamily: 'var(--mono)' }}>₹{m.hourlyRate}/hr</td>
                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>₹{m.budgetSpent.toLocaleString()}</td>
                    <td>
                      <span className={m.qualityScore >= 80 ? 'pill pg' : m.qualityScore >= 60 ? 'pill pa' : 'pill pr'}>
                        {m.qualityScore}/100
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                        <button className="btn-s" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => openLogs(m)}>Logs</button>
                        <button className="btn-s" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => openProfile(m)}>Profile</button>
                        {!m.isOwner && (
                          <button className="btn-d" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => void removeMember(m)}>Remove</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Kudos & Peer Recognition Board */}
        <KudosConfettiCanvas active={confettiActive} onComplete={() => setConfettiActive(false)} />
        
        <div className="kudos-container-grid">
          {/* Left Column: Kudos Feed */}
          <div className="card bg-white dark:bg-zinc-900 border border-slate-100 dark:border-zinc-800 rounded-2xl p-6 shadow-sm flex flex-col gap-5" style={{ padding: '24px', boxSizing: 'border-box' }}>
            <div className="flex flex-col items-center justify-center pb-3 border-b border-zinc-150 dark:border-zinc-800/80 text-center gap-1">
              <span className="text-xs font-black uppercase text-zinc-500 tracking-wider flex items-center justify-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                  <path d="M4 22h16" />
                  <path d="M10 14.66V17c0 .55-.45 1-1 1H4v2h16v-2h-5c-.55 0-1-.45-1-1v-2.34" />
                  <path d="M12 2a5 5 0 0 0-5 5v3c0 2.76 2.24 5 5 5s5-2.24 5-5V7a5 5 0 0 0-5-5z" />
                </svg>
                <span>Recent Peer Kudos</span>
              </span>
              <span className="text-[8.5px] font-bold text-zinc-400 font-mono tracking-wide uppercase">Real-time Recognition</span>
            </div>

            <div className="space-y-4.5 max-h-[380px] overflow-y-auto pr-1 custom-scrollbar">
              {kudosList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--tm-text-muted)' }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>✨</div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--tm-text)' }}>No peer kudos awarded yet</div>
                  <div style={{ fontSize: '0.78rem', marginTop: '0.25rem' }}>Recognize your team members for their outstanding engineering and collaboration!</div>
                </div>
              ) : (
                kudosList.map((k, i) => (
                  <div 
                    key={i} 
                    style={{
                      padding: '16px 20px',
                      borderRadius: '12px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg2)',
                      display: 'flex',
                      alignItems: 'start',
                      gap: '14px',
                      boxSizing: 'border-box',
                      marginBottom: '12px'
                    }}
                    className="hover:scale-[1.01] transition-all"
                  >
                    <div 
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: 'var(--accent)',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        flexShrink: 0
                      }}
                    >
                      {k.sender.split(' ').map(x => x[0]).join('')}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text)' }}>
                          {k.sender} → <span style={{ color: 'var(--accent)' }}>{k.recipient}</span>
                        </span>
                        <span style={{ fontSize: '9px', color: 'var(--text3)', fontFamily: 'var(--mono)', flexShrink: 0, marginLeft: 'auto' }}>
                          {k.date}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px' }}>
                        <span style={{ 
                          fontSize: '9.5px', 
                          fontWeight: 'bold', 
                          background: 'rgba(99, 102, 241, 0.08)', 
                          color: 'var(--accent)', 
                          padding: '2px 8px', 
                          borderRadius: '6px', 
                          border: '1px solid rgba(99, 102, 241, 0.15)' 
                        }}>
                          {k.badge}
                        </span>
                      </div>
                      <p style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '10px', lineHeight: '1.6', fontWeight: '500', fontStyle: 'italic', margin: '10px 0 0 0' }}>
                        "{k.note}"
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Column: Give Kudos Form */}
          <div className="card bg-white dark:bg-zinc-900 border border-slate-100 dark:border-zinc-800 rounded-2xl p-6 shadow-sm flex flex-col gap-5" style={{ padding: '24px', boxSizing: 'border-box' }}>
            <div className="flex flex-col items-center justify-center pb-3 border-b border-zinc-150 dark:border-zinc-800/80 text-center gap-1">
              <span className="text-xs font-black uppercase text-zinc-500 tracking-wider flex items-center justify-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-500 animate-pulse">
                  <circle cx="12" cy="8" r="7" />
                  <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                </svg>
                <span>Award Kudos Badge</span>
              </span>
            </div>

            <form onSubmit={handleSendKudos} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Select Peer</label>
                <select
                  value={kudosTarget}
                  onChange={e => setKudosTarget(e.target.value)}
                  className="kudos-give-select"
                >
                  <option value="">-- Choose colleague --</option>
                  {list.map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({ROLE_LABEL[m.role]})</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Kudos Badge</label>
                <select
                  value={kudosBadge}
                  onChange={e => setKudosBadge(e.target.value)}
                  className="kudos-give-select"
                >
                  <option value="🧠 Code Mentor">🧠 Code Mentor</option>
                  <option value="🐛 Bug Squasher">🐛 Bug Squasher</option>
                  <option value="🏆 Sprint Champion">🏆 Sprint Champion</option>
                  <option value="🌟 Rising Star">🌟 Rising Star</option>
                  <option value="🤝 Synergy Builder">🤝 Synergy Builder</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">Appreciation Note</label>
                <textarea
                  value={kudosNote}
                  onChange={e => setKudosNote(e.target.value)}
                  placeholder="What did they do that was awesome?"
                  rows={2}
                  className="kudos-give-textarea"
                />
              </div>

              <button 
                type="submit"
                className="kudos-give-btn"
              >
                <span>Award Kudos 🏆</span>
              </button>
            </form>
          </div>
        </div>

        {/* Pending invites */}
        <div className={`card fu${mounted ? ' in' : ''}`} style={{ transitionDelay: '.25s' }}>
          <div className="ch"><span className="ct">Pending invites</span></div>
          {pendingInvites.map(inv => (
            <div key={inv.id} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text)' }}>{inv.email}</div>
                <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', marginTop: 2 }}>
                  invited {Math.max(1, Math.round((Date.now() - new Date(inv.createdAt).getTime()) / 3600000))}h ago · {ROLE_LABEL[inv.role].toLowerCase()} · ₹{inv.hourlyRate}/hr
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="pill pa">Pending</span>
                {inv.joinUrl && (
                  <button className="clink" onClick={() => copyText(inv.joinUrl || '', 'Invite link copied')}>Copy link</button>
                )}
                <button className="clink" onClick={() => void resendInvite(inv)}>Resend</button>
              </div>
            </div>
          ))}
          <div style={{ padding: '11px 16px', fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>
            {pendingInvites.length ? 'Pending teammates will appear in active directory once they accept.' : 'No pending invites.'}
          </div>
        </div>
      </div>

      {contextMenu && (
        <div
          className="td-ctx-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="td-ctx-header">
            <div className="td-ctx-av" style={{ background: AVATAR_BG[contextMenu.member.role], color: AVATAR_FG[contextMenu.member.role] }}>{contextMenu.member.initials}</div>
            <div>
              <div className="td-ctx-name">{contextMenu.member.name}</div>
              <div className="td-ctx-role">{ROLE_LABEL[contextMenu.member.role]}</div>
            </div>
          </div>
          <button className="td-ctx-item" onClick={() => ctxAction('profile')}>View Profile</button>
          <button className="td-ctx-item" onClick={() => ctxAction('logs')}>View Logs</button>
          <button className="td-ctx-item td-ctx-blue" onClick={() => ctxAction('assign')}>Assign Task</button>
          <div className="td-ctx-sep" />
          <button className="td-ctx-item" onClick={() => ctxAction('message')}>Send Message</button>
          <button className="td-ctx-item" onClick={() => ctxAction('report')}>View Report</button>
          {contextMenu.member.email && (
            <button className="td-ctx-item" onClick={() => { copyText(contextMenu.member.email || '', 'Email copied'); setContextMenu(null); }}>Copy Email</button>
          )}
          {!contextMenu.member.isOwner && (
            <>
              <div className="td-ctx-sep" />
              <button className="td-ctx-item td-ctx-red" onClick={() => ctxAction('remove')}>Remove Member</button>
            </>
          )}
        </div>
      )}

      {showInviteModal && (
        <div className="overlay open" onClick={(e) => { if (e.target === e.currentTarget) { setShowInviteModal(false); setInviteResult(null); setInviteForm({ email: '', role: 'dev', hourlyRate: 500, message: '' }); } }}>
          <div className="modal">
            <div className="mhdr">
              <div>
                <div className="mttl">Invite a team member</div>
                <div className="msub">They&apos;ll receive an email with an invite link</div>
              </div>
              <button className="mclose" onClick={() => { setShowInviteModal(false); setInviteResult(null); setInviteForm({ email: '', role: 'dev', hourlyRate: 500, message: '' }); }}>×</button>
            </div>
            <div className="mbody">
              {!inviteResult && (
                <>
                  <div className="fgroup"><label className="label">Email address</label><input className="inp" placeholder="dev@company.com" value={inviteForm.email} onChange={(e) => setInviteForm(p => ({ ...p, email: e.target.value }))} /></div>
                  <div className="fgroup">
                    <label className="label">Role</label>
                    <select
                      className="inp"
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm(p => ({ ...p, role: e.target.value as TeamRole }))}
                    >
                      <option value="dev">Developer (Engineer)</option>
                      <option value="lead">Team Lead</option>
                      <option value="designer">UI/UX Designer</option>
                      <option value="qa">QA Engineer</option>
                      <option value="devops">DevOps Engineer</option>
                      <option value="pm">Product Manager</option>
                    </select>
                  </div>
                  <div className="fgroup"><label className="label">Hourly rate (INR/hr)</label><input className="inp" placeholder="500" type="number" value={inviteForm.hourlyRate} onChange={(e) => setInviteForm(p => ({ ...p, hourlyRate: Number(e.target.value) || 0 }))} /></div>
                  <div className="fgroup" style={{ marginBottom: 0 }}><label className="label">Message (optional)</label><textarea className="inp" rows={2} placeholder="Add a note to your invite..." value={inviteForm.message} onChange={(e) => setInviteForm(p => ({ ...p, message: e.target.value }))} /></div>
                </>
              )}
              {inviteResult && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ padding: 14, borderRadius: 10, background: inviteResult.inviteEmailSent ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${inviteResult.inviteEmailSent ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}` }}>
                    <div style={{ fontWeight: 600, color: inviteResult.inviteEmailSent ? 'var(--green)' : 'var(--red)' }}>
                      {inviteResult.inviteEmailSent ? '✅ Invite email sent' : '⚠️ Invite created, but email failed'}
                    </div>
                    {inviteResult.inviteEmailError && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)' }}>{inviteResult.inviteEmailError}</div>
                    )}
                    {inviteResult.inviteEmailSent && inviteResult.inviteEmailProviderId && (
                      <div style={{ marginTop: 6, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)' }}>Provider ID: {inviteResult.inviteEmailProviderId}</div>
                    )}
                  </div>
                  {inviteResult.joinUrl && (
                    <div className="fgroup" style={{ marginBottom: 0 }}>
                      <label className="label">Invite link</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="inp" value={inviteResult.joinUrl} readOnly style={{ flex: 1, fontSize: 12 }} />
                        <button className="btn-s" onClick={() => copyText(inviteResult.joinUrl || '', 'Invite link copied')}>Copy</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mfoot">
              {!inviteResult && (
                <>
                  <button className="btn-s" onClick={() => { setShowInviteModal(false); setInviteForm({ email: '', role: 'dev', hourlyRate: 500, message: '' }); }}>Cancel</button>
                  <button className="btn-p" onClick={() => void submitInvite()} disabled={inviteBusy}>{inviteBusy ? 'Sending...' : 'Send invite ->'}</button>
                </>
              )}
              {inviteResult && (
                <>
                  <button className="btn-s" onClick={() => { setInviteResult(null); setInviteForm({ email: '', role: 'dev', hourlyRate: 500, message: '' }); }}>Send another</button>
                  <button className="btn-p" onClick={() => { setShowInviteModal(false); setInviteResult(null); setInviteForm({ email: '', role: 'dev', hourlyRate: 500, message: '' }); }}>Done</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {profileMember && (
        <div className="td-profile-overlay" onClick={(e) => { if (e.target === e.currentTarget) setProfileMember(null); }}>
          <div className="td-profile-modal">
            <div className="td-profile-head">
              <div className="td-profile-av" style={{ background: AVATAR_BG[profileMember.role], color: AVATAR_FG[profileMember.role] }}>{profileMember.initials}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="td-profile-name">{profileMember.name}</div>
                  <span className="td-profile-pill">{profileBadge}</span>
                </div>
                <div className="td-profile-meta">{ROLE_LABEL[profileMember.role]} / Developer · INR {profileMember.hourlyRate}/hr · {profileMember.hoursThisWeek}h this sprint · INR {profileMember.budgetSpent.toLocaleString()} cost</div>
              </div>
              <div className="td-profile-ring-wrap">
                <svg width="54" height="54" viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="27" cy="27" r="20" fill="none" stroke="var(--bg3)" strokeWidth="5" />
                  <circle cx="27" cy="27" r="20" fill="none" stroke="var(--blue)" strokeWidth="5" strokeLinecap="round" strokeDasharray="125.7" strokeDashoffset={125.7 * (1 - profileComposite / 100)} />
                </svg>
                <div className="td-profile-ring-center">
                  <div className="td-profile-ring-score">{profileComposite}</div>
                  <div className="td-profile-ring-label">score</div>
                </div>
              </div>
              <button className="td-profile-close" onClick={() => setProfileMember(null)}>✕</button>
            </div>

            <div className="td-profile-tabs">
              <button className={`td-profile-tab${profileTab === 'overview' ? ' active' : ''}`} onClick={() => setProfileTab('overview')}>Overview</button>
              <button className={`td-profile-tab${profileTab === 'tasks' ? ' active' : ''}`} onClick={() => setProfileTab('tasks')}>Tasks</button>
              <button className={`td-profile-tab${profileTab === 'logs' ? ' active' : ''}`} onClick={() => setProfileTab('logs')}>Time Logs</button>
              <button className={`td-profile-tab${profileTab === 'report' ? ' active' : ''}`} onClick={() => setProfileTab('report')}>Report</button>
            </div>

            <div className="td-profile-body">
              {profileTab === 'overview' && (
                <>
                  <div className="td-profile-grid">
                    <div className="td-profile-stat"><div className="dp-stat-lbl">Code Quality</div><div className="dp-stat-val" style={{ color: profileMember.qualityScore >= 80 ? 'var(--green)' : profileMember.qualityScore >= 60 ? 'var(--amber)' : 'var(--red)' }}>{profileMember.qualityScore}<span className="dp-stat-sfx">/100</span></div><div className="dp-stat-sub">avg this sprint</div></div>
                    <div className="td-profile-stat"><div className="dp-stat-lbl">Efficiency Score</div><div className="dp-stat-val" style={{ color: 'var(--blue)' }}>{profileComposite}<span className="dp-stat-sfx">/100</span></div><div className="dp-stat-sub">composite rating</div></div>
                    <div className="td-profile-stat"><div className="dp-stat-lbl">Tasks Done</div><div className="dp-stat-val" style={{ color: 'var(--green)' }}>{profileTasks.done.length}</div><div className="dp-stat-sub">{profileTasks.pending.length} pending</div></div>
                    <div className="td-profile-stat"><div className="dp-stat-lbl">PRs Merged</div><div className="dp-stat-val">{profileMember.prs ?? 0}</div><div className="dp-stat-sub">{profileTasks.done.length + profileTasks.pending.length} commits total</div></div>
                  </div>

                  <div className="dp-section-title">Performance breakdown</div>
                  <div className="td-profile-bar-row"><span>Code Quality</span><span>{profileMember.qualityScore}/100</span></div>
                  <div className="td-profile-bar"><div style={{ width: `${profileMember.qualityScore}%`, background: profileMember.qualityScore >= 80 ? 'var(--green)' : profileMember.qualityScore >= 60 ? 'var(--amber)' : 'var(--red)' }} /></div>
                  <div className="td-profile-bar-row"><span>Commit Velocity</span><span>{profilePerformance.velocity}/100</span></div>
                  <div className="td-profile-bar"><div style={{ width: `${profilePerformance.velocity}%`, background: 'var(--blue)' }} /></div>
                  <div className="td-profile-bar-row"><span>PR Success Rate</span><span>{profilePerformance.prRate}/100</span></div>
                  <div className="td-profile-bar"><div style={{ width: `${profilePerformance.prRate}%`, background: 'var(--green)' }} /></div>
                  <div className="td-profile-bar-row"><span>Hours Utilisation</span><span>{profilePerformance.utilization}/100</span></div>
                  <div className="td-profile-bar"><div style={{ width: `${profilePerformance.utilization}%`, background: 'var(--amber)' }} /></div>

                  <div className="dp-section-title">Task summary</div>
                  <div className="td-profile-summary-grid">
                    <div className="td-profile-summary-card ok"><div className="td-profile-summary-num">{profileTasks.done.length}</div><div className="td-profile-summary-lbl">completed</div></div>
                    <div className="td-profile-summary-card warn"><div className="td-profile-summary-num">{profileTasks.pending.length}</div><div className="td-profile-summary-lbl">pending</div></div>
                    <div className="td-profile-summary-card"><div className="td-profile-summary-num">{profileMember.hoursThisWeek}h</div><div className="td-profile-summary-lbl">logged</div></div>
                  </div>
                </>
              )}

              {profileTab === 'tasks' && (
                <div>
                  {profileTasks.done.concat(profileTasks.pending).map((task, idx) => (
                    <div key={`${task.name}-${idx}`} className="td-profile-row">
                      <div className={`td-profile-dot ${task.pending ? 'pending' : 'done'}`} />
                      <div style={{ flex: 1 }}>
                        <div className="td-profile-row-title">{task.name}</div>
                        <div className="td-profile-row-meta">{task.project} · {task.date}</div>
                      </div>
                      <div className="td-profile-row-hours">{task.hrs > 0 ? `${task.hrs}h` : '-'}</div>
                    </div>
                  ))}
                </div>
              )}

              {profileTab === 'logs' && (
                <div>
                  {profileLogs.map((log, idx) => (
                    <div key={`${log.title}-${idx}`} className="td-profile-row">
                      <div className={`td-profile-dot ${log.dot}`} />
                      <div style={{ flex: 1 }}>
                        <div className="td-profile-row-title">{log.title}</div>
                        <div className="td-profile-row-meta">{log.desc}</div>
                      </div>
                      <div className="td-profile-row-hours">{log.hrs}h</div>
                    </div>
                  ))}
                </div>
              )}

              {profileTab === 'report' && (() => {
                const tasksTotal = (profileTasks.done.length + profileTasks.pending.length) || 8;
                const tasksDone = profileTasks.done.length || 5;
                const taskPct = Math.round((tasksDone / tasksTotal) * 100);
                const hoursLogged = profileMember.hoursThisWeek || 12.5;
                const hourlyRate = profileMember.hourlyRate || 500;
                const totalCost = Math.round(hoursLogged * hourlyRate);
                const qualityMetric = profileMember.qualityScore;
                const efficiencyMetric = profileComposite;
                const qualityPer = Math.max(0, Math.round(qualityMetric - (hoursLogged * 0.5)));
                const sprintNum = 4;
                const summaryLine = efficiencyMetric >= 85
                  ? 'High performer — no action needed.'
                  : efficiencyMetric >= 65
                    ? 'Good progress — minor optimizations suggested.'
                    : 'Needs attention — review workload distribution.';
                return (
                  <div className="td-profile-report">
                    {/* Report header card */}
                    <div className="rpt-header-card">
                      <div className="rpt-header-eyebrow">ADMIN REPORT · SPRINT {sprintNum}</div>
                      <div className="rpt-header-name">{profileMember.name}</div>
                      <div className="rpt-header-summary">{summaryLine}</div>
                    </div>

                    {/* Sprint Metrics */}
                    <div className="rpt-section-label">SPRINT METRICS</div>
                    <div className="rpt-metrics-grid">
                      <div className="rpt-metric-card">
                        <div className="rpt-metric-val" style={{ color: 'var(--blue)' }}>{qualityMetric}</div>
                        <div className="rpt-metric-sub">quality/100</div>
                      </div>
                      <div className="rpt-metric-card">
                        <div className="rpt-metric-val" style={{ color: 'var(--green)' }}>{efficiencyMetric}</div>
                        <div className="rpt-metric-sub">efficiency/100</div>
                      </div>
                      <div className="rpt-metric-card">
                        <div className="rpt-metric-val">{qualityPer}</div>
                        <div className="rpt-metric-sub">quality/₹k</div>
                      </div>
                    </div>

                    {/* Task completion */}
                    <div className="rpt-section-label">TASK COMPLETION RATE</div>
                    <div className="rpt-completion-card">
                      <div className="rpt-completion-row">
                        <span>{tasksDone} of {tasksTotal} tasks done</span>
                        <span style={{ color: taskPct >= 75 ? 'var(--green)' : taskPct >= 50 ? 'var(--amber)' : 'var(--red)', fontWeight: 700, fontFamily: 'var(--mono)' }}>{taskPct}%</span>
                      </div>
                      <div className="rpt-completion-bar">
                        <div style={{ width: `${taskPct}%`, background: taskPct >= 75 ? 'var(--green)' : taskPct >= 50 ? 'var(--amber)' : 'var(--red)' }} />
                      </div>
                    </div>

                    {/* Cost summary */}
                    <div className="rpt-section-label">COST SUMMARY</div>
                    <div className="rpt-cost-table">
                      <div className="rpt-cost-row"><span>Hours logged</span><span>{hoursLogged}h</span></div>
                      <div className="rpt-cost-row"><span>Hourly rate</span><span>₹{hourlyRate}/hr</span></div>
                      <div className="rpt-cost-row total"><span>Total cost</span><span style={{ color: 'var(--blue)' }}>₹{totalCost.toLocaleString()}</span></div>
                    </div>

                    {/* Actions */}
                    <div className="rpt-actions">
                      <button className="rpt-btn">Export PDF</button>
                      <button className="rpt-btn secondary">View in Reports →</button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div className={`td-logs-overlay${logsMember ? ' open' : ''}`}>
        <div className="td-logs-backdrop" onClick={() => setLogsMember(null)} />
        <div className="td-logs-panel">
          {logsMember && (
            <>
              <div className="td-logs-header">
                <div className="td-logs-av" style={{ background: AVATAR_BG[logsMember.role], color: AVATAR_FG[logsMember.role] }}>{logsMember.initials}</div>
                <div>
                  <div className="td-logs-name">{logsMember.name}</div>
                  <div className="td-logs-meta">{ROLE_LABEL[logsMember.role].toLowerCase()} · {logsMember.hoursThisWeek} hrs this week · ₹{logsMember.hourlyRate}/hr</div>
                </div>
                <button className="td-logs-close" onClick={() => setLogsMember(null)}>×</button>
              </div>

              <div className="td-logs-stats">
                <div className="td-logs-stat"><div className="td-logs-stat-eye">Hours</div><div className="td-logs-stat-val">{logsMember.hoursThisWeek} hrs</div></div>
                <div className="td-logs-stat"><div className="td-logs-stat-eye">Spent</div><div className="td-logs-stat-val">₹{logsMember.budgetSpent.toLocaleString()}</div></div>
                <div className="td-logs-stat"><div className="td-logs-stat-eye">PRs</div><div className="td-logs-stat-val">{logsMember.prs ?? logsPack.days.flatMap(d => d.entries).filter(e => e.type === 'pr').length} PRs</div></div>
                <div className="td-logs-stat"><div className="td-logs-stat-eye">Quality</div><div className="td-logs-stat-val">{logsMember.qualityScore}/100</div></div>
              </div>

              <div className="td-logs-toolbar">
                <button className={`td-log-filter${logsFilter === 'all' ? ' active' : ''}`} onClick={() => setLogsFilter('all')}>All</button>
                <button className={`td-log-filter${logsFilter === 'commit' ? ' active' : ''}`} onClick={() => setLogsFilter('commit')}>Commits</button>
                <button className={`td-log-filter${logsFilter === 'pr' ? ' active' : ''}`} onClick={() => setLogsFilter('pr')}>Pull requests</button>
                <button className={`td-log-filter${logsFilter === 'log' ? ' active' : ''}`} onClick={() => setLogsFilter('log')}>Hour logs</button>
                <button className={`td-log-filter${logsFilter === 'review' ? ' active' : ''}`} onClick={() => setLogsFilter('review')}>Reviews</button>
                <input className="td-log-search" placeholder="Search logs..." value={logsQuery} onChange={(e) => setLogsQuery(e.target.value)} />
              </div>

              <div className="td-logs-body">
                {visibleLogDays.length === 0 && (
                  <div className="td-team-empty">No logs match this filter.</div>
                )}
                {visibleLogDays.map(day => (
                  <div key={day.day}>
                    <div className="td-log-day-header">{day.day}</div>
                    {day.entries.map((entry, idx) => (
                      <div key={`${day.day}-${entry.time}-${idx}`} className="td-log-entry">
                        <div className="td-log-time-col">{entry.time}</div>
                        <div className={`td-log-dot ${entry.dot}`} />
                        <div className="td-log-main">
                          <div className="td-log-title">{entry.title}</div>
                          <div className="td-log-desc">{entry.desc}</div>
                          <div className="td-log-tags">
                            {entry.tags.map((tag, tIdx) => (
                              <span key={`${tag.t}-${tIdx}`} className={`td-log-tag ${tag.c}`}>{tag.t}</span>
                            ))}
                          </div>
                        </div>
                        <div className="td-log-right">
                          <div className="td-log-hrs">{entry.hrs}h</div>
                          <div className="td-log-cost">₹{entry.cost.toLocaleString('en-IN')}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="td-logs-footer">
                <span>{logsSummary.count} entr{logsSummary.count === 1 ? 'y' : 'ies'} · {logsSummary.totalHrs.toFixed(1)} hrs</span>
                <span className="td-logs-total">Total: ₹{logsSummary.totalCost.toLocaleString('en-IN')}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
